import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAppState } from '../core/redux/hooks';
import { useMount } from '../core/hooks/useMount';
import { useAnyLoading } from '../core/api/hooks';
import { ActionType } from '../core/redux/actionType';
import { useRoutes } from '../navigation/routes';
import * as ReactDOM from 'react-dom';
import { Spinner } from '../loading/Spinner';
import { COLORS } from '../globalStyle/colors';
import { css } from '@emotion/core';
import { Z_INDEX } from '../globalStyle/zIndex';
import { reportRecipientReportsAction } from './api/reportRecipientReportsAction';
import { reportRecipientParticipantsAction } from './api/reportRecipientParticipantsAction';
import { useCurrentUser } from '../auth/hooks';
import { reportRecipientProjectsAction } from './api/reportRecipientProjectsAction';


const styles = {
  root:    css`
    position: fixed;
    z-index: ${Z_INDEX.MODAL};

    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;

    display: flex;
    align-items: center;
    justify-content: center;

    background-color: rgba(0, 0, 0, 0.8);
    cursor: pointer;
    opacity: 0;
  `,
  spinner: css`
    width: 100px;
    height: 100px;
  `,
};


/**
 * ReportRecipientRedirect: a component that fetches data for the report recipient flow before loading its respective
 * UI.
 *
 * Shows a loading screen while data is being retrieved.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ReportRecipientRedirect: React.FC = () => {
  const dispatch = useDispatch();
  const history  = useHistory();

  const currentUser = useCurrentUser();
  const recipientId = React.useMemo(() => currentUser?.personId ?? -1, [currentUser]);
  const userName  = React.useMemo(() => currentUser?.userName || '', [currentUser]);

  const reports      = useAppState(state => state.reportRecipientReports);
  const participants = useAppState(state => state.reportRecipientParticipants);
  const projects     = useAppState(state => state.reportRecipientProjects);
  const routes       = useRoutes();


  useMount(() => {
    if (!reports) {
      dispatch(reportRecipientReportsAction(recipientId, userName));
    }
    if (!participants) {
      dispatch(reportRecipientParticipantsAction(recipientId));
    }
    if (!projects) {
      dispatch(reportRecipientProjectsAction(recipientId));
    }
  });

  const isLoading = useAnyLoading([
    ActionType.ReportRecipientReportsRequest,
    ActionType.ReportRecipientParticipantsRequest,
    ActionType.ReportRecipientProjectsRequest,
  ]);

  if (!!reports && !!participants && !!projects) {
    history.replace(routes.ReportRecipientDashboard);
  }

  return (!reports || !participants || !projects || isLoading)
    ? (
      ReactDOM.createPortal(
        <div css={styles.root}>
          <Spinner fill={COLORS.GREEN} css={styles.spinner} />
        </div>,
        document.body,
      )
    ) : null;
};

export default ReportRecipientRedirect;
